import React, { useState, useEffect, useCallback } from 'react';
import { Instagram, Loader2, CheckCircle2, AlertTriangle, Star, KeyRound, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusRow = ({ result }) => {
  const ok = result.status === 'imported';
  return (
    <li
      data-testid={`ig-result-${result.status}`}
      className={`flex items-start gap-2 px-3 py-2 rounded border text-xs ${
        ok
          ? 'bg-emerald-900/25 border-emerald-500/40 text-emerald-200'
          : 'bg-red-900/25 border-red-500/40 text-red-300'
      }`}
    >
      {ok ? (
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-bold break-words">{result.url}</p>
        {ok ? (
          <p className="mt-0.5 text-emerald-300/90">
            <span className="font-black">{result.title}</span>
            {result.description && (
              <span className="block text-emerald-200/70 mt-0.5 line-clamp-2">{result.description}</span>
            )}
          </p>
        ) : (
          <p className="mt-0.5 text-red-300/90">{result.error || 'Unknown error'}</p>
        )}
      </div>
    </li>
  );
};

const InstagramImportPanel = ({ token, onImported }) => {
  const [raw, setRaw] = useState('');
  const [isExclusive, setIsExclusive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // --- Cookies (session auth for sensitive content) ---
  const [cookieStatus, setCookieStatus] = useState({ configured: false });
  const [showCookies, setShowCookies] = useState(false);
  const [cookieText, setCookieText] = useState('');
  const [cookieBusy, setCookieBusy] = useState(false);
  const [cookieMsg, setCookieMsg] = useState('');

  const auth = { Authorization: `Bearer ${token}` };

  const loadCookieStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/import/instagram/cookies/status`, { headers: auth });
      if (res.ok) setCookieStatus(await res.json());
    } catch (e) {
      console.error('Cookie status load failed:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadCookieStatus(); }, [loadCookieStatus]);

  const saveCookies = async () => {
    setCookieBusy(true);
    setCookieMsg('');
    try {
      const res = await fetch(`${API}/api/admin/import/instagram/cookies`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: cookieText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setCookieMsg(`Saved (${data.size} chars).`);
      setCookieText('');
      await loadCookieStatus();
    } catch (e) {
      setCookieMsg(`Error: ${e.message}`);
    } finally {
      setCookieBusy(false);
      setTimeout(() => setCookieMsg(''), 4000);
    }
  };

  const clearCookies = async () => {
    if (!window.confirm('Remove the stored Instagram session cookies?')) return;
    setCookieBusy(true);
    try {
      await fetch(`${API}/api/admin/import/instagram/cookies`, { method: 'DELETE', headers: auth });
      await loadCookieStatus();
      setCookieMsg('Cookies cleared.');
    } catch (e) {
      setCookieMsg(`Error: ${e.message}`);
    } finally {
      setCookieBusy(false);
      setTimeout(() => setCookieMsg(''), 3000);
    }
  };

  const urls = raw
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const handleImport = async () => {
    if (urls.length === 0) return;
    setBusy(true);
    setError('');
    setResults(null);
    try {
      const res = await fetch(`${API}/api/admin/import/instagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ urls, is_exclusive: isExclusive }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Import failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setResults(data);
      const importedCount = data.filter((r) => r.status === 'imported').length;
      if (importedCount > 0) {
        setRaw('');
        if (typeof onImported === 'function') onImported();
      }
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="instagram-import-panel"
      className="relative bg-gradient-to-br from-pink-950/30 via-zinc-900/80 to-black border border-pink-700/40 rounded-2xl p-5 md:p-6 mb-6 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -top-10 -right-10 w-40 h-40 bg-pink-500/15 blur-3xl pointer-events-none"
      />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Instagram className="h-5 w-5 text-pink-400" />
          <h3 className="text-lg md:text-xl font-black text-white tracking-tight">
            Import from <span className="text-pink-400">Instagram</span>
          </h3>
          {cookieStatus.configured ? (
            <span
              data-testid="ig-cookies-on"
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-black tracking-widest bg-emerald-700/30 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded-full uppercase"
            >
              <KeyRound className="h-3 w-3" />
              Auth ON
            </span>
          ) : (
            <span
              data-testid="ig-cookies-off"
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-black tracking-widest bg-yellow-700/20 text-yellow-300 border border-yellow-500/40 px-2 py-0.5 rounded-full uppercase"
            >
              <KeyRound className="h-3 w-3" />
              No Auth
            </span>
          )}
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Paste one or more public Instagram Reel/Post URLs (one per line). Videos download to{' '}
          <span className="text-white font-bold">Cloudflare R2</span>, captions strip hashtags
          automatically.
        </p>

        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={'https://www.instagram.com/reel/...\nhttps://www.instagram.com/p/...'}
          rows={4}
          disabled={busy}
          data-testid="instagram-import-textarea"
          className="w-full bg-black/50 border border-pink-900/40 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-pink-500 font-mono resize-y disabled:opacity-60"
        />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3">
          <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isExclusive}
              onChange={(e) => setIsExclusive(e.target.checked)}
              disabled={busy}
              data-testid="instagram-import-exclusive"
              className="h-4 w-4 accent-yellow-500 cursor-pointer"
            />
            <Star className="h-3.5 w-3.5 text-yellow-400" />
            Mark all as <span className="text-yellow-300 font-bold">Exclusive</span>
          </label>

          <button
            type="button"
            onClick={handleImport}
            disabled={busy || urls.length === 0}
            data-testid="instagram-import-btn"
            className="inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black tracking-wide px-5 py-2.5 rounded-lg transition-colors shadow-[0_0_18px_rgba(236,72,153,0.4)]"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                IMPORTING {urls.length}…
              </>
            ) : (
              <>
                <Instagram className="h-4 w-4" />
                IMPORT {urls.length || ''} URL{urls.length === 1 ? '' : 's'}
              </>
            )}
          </button>
        </div>

        {error && (
          <div
            data-testid="instagram-import-error"
            className="mt-4 bg-red-900/30 border border-red-500/50 text-red-300 text-xs px-3 py-2 rounded"
          >
            {error}
          </div>
        )}

        {results && (
          <ul className="mt-4 space-y-2" data-testid="instagram-import-results">
            {results.map((r, idx) => (
              <StatusRow key={`${r.url}-${idx}`} result={r} />
            ))}
          </ul>
        )}

        <p className="text-[10px] text-gray-500 mt-4 leading-snug">
          Imports happen one at a time on the server (yt-dlp → R2). Allow a few seconds per video.
          Private accounts and stories aren't supported in this flow.
        </p>

        {/* Cookies / auth manager - collapsed by default */}
        <div className="mt-5 pt-4 border-t border-pink-900/40">
          <button
            type="button"
            onClick={() => setShowCookies((v) => !v)}
            data-testid="ig-cookies-toggle"
            className="w-full flex items-center justify-between gap-2 text-xs font-black tracking-[0.2em] uppercase text-gray-300 hover:text-white"
          >
            <span className="inline-flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5 text-pink-400" />
              Instagram Session Cookies
              {cookieStatus.configured && (
                <span className="text-emerald-400 normal-case tracking-normal text-[10px]">
                  · saved {cookieStatus.size} chars
                </span>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showCookies ? 'rotate-180' : ''}`} />
          </button>

          {showCookies && (
            <div className="mt-3 space-y-3" data-testid="ig-cookies-panel">
              <p className="text-xs text-gray-400 leading-relaxed">
                Instagram blocks anonymous access to age-restricted / sensitive posts. Paste the
                contents of a <span className="text-white font-bold">Netscape cookies.txt</span> file
                exported from a logged-in Instagram tab (use the{' '}
                <span className="font-mono text-pink-300">"Get cookies.txt LOCALLY"</span> browser
                extension). Cookies are stored on the server only - never sent to anyone else.
              </p>
              <textarea
                value={cookieText}
                onChange={(e) => setCookieText(e.target.value)}
                placeholder={'# Netscape HTTP Cookie File\n.instagram.com\tTRUE\t/\tTRUE\t...'}
                rows={5}
                disabled={cookieBusy}
                data-testid="ig-cookies-textarea"
                className="w-full bg-black/60 border border-pink-900/40 text-gray-100 px-3 py-2 rounded text-xs focus:outline-none focus:border-pink-500 font-mono resize-y disabled:opacity-60"
              />
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                {cookieStatus.configured && (
                  <button
                    type="button"
                    onClick={clearCookies}
                    disabled={cookieBusy}
                    data-testid="ig-cookies-clear-btn"
                    className="bg-zinc-900 hover:bg-zinc-800 border border-red-900/40 text-red-300 hover:text-white font-bold text-xs px-3 py-2 rounded disabled:opacity-50"
                  >
                    Clear stored cookies
                  </button>
                )}
                <button
                  type="button"
                  onClick={saveCookies}
                  disabled={cookieBusy || !cookieText.trim()}
                  data-testid="ig-cookies-save-btn"
                  className="bg-pink-600 hover:bg-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-xs tracking-wide uppercase px-4 py-2 rounded inline-flex items-center gap-2"
                >
                  {cookieBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                  {cookieBusy ? 'Saving…' : 'Save Cookies'}
                </button>
              </div>
              {cookieMsg && (
                <p
                  data-testid="ig-cookies-msg"
                  className={`text-xs ${cookieMsg.startsWith('Error') ? 'text-red-300' : 'text-emerald-300'}`}
                >
                  {cookieMsg}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InstagramImportPanel;
